import { horasTrabajadasEfectivas } from './horasTareoSimple';
import { conceptoTareoAplicaACanal } from './ajusteConceptoDestino';

function ymd(valor) {
  if (!valor) return null;
  const s = String(valor);
  if (s.includes('T')) return s.split('T')[0];
  if (s.includes(' ')) return s.split(' ')[0];
  return s;
}

function compactarHistorial(regs) {
  if (!Array.isArray(regs)) return [];
  return regs
    .filter((h) => h && (h.fecha_ingreso || h.fechaIngreso))
    .map((h) => ({
      id: h.id || null,
      fecha_ingreso: ymd(h.fecha_ingreso || h.fechaIngreso),
      fecha_cese: ymd(h.fecha_cese || h.fechaCese),
      motivo_cese: h.motivo_cese || h.motivoCese || null,
    }));
}

function compactarConcepto(c, index = 0) {
  if (!c || typeof c !== 'object') return null;
  const codigo = String(
    c.codigoPlame
    || c.codigo_plame
    || c.codigo
    || c.plame_configuracion?.codigo_plame
    || c.id_concepto?.codigo_plame
    || ''
  );
  const monto = Number(
    c.monto ?? c.valor ?? c.monto_interna ?? c.monto_calculado ?? 0
  );
  return {
    id: c.id || `snap_${codigo || index}`,
    codigo,
    codigo_plame: codigo,
    codigoPlame: codigo,
    descripcion: c.descripcion || c.nombre || c.concepto || c.id_concepto?.descripcion || c.id_concepto?.nombre || codigo,
    monto,
    valor: monto,
    tipo_concepto: c.tipo_concepto || c.tipoConcepto || null,
    base_calculo: Number(c.base_calculo || c.base_imponible || 0),
    destino_ajuste: c.destino_ajuste || c.destinoAjuste || null,
    excluirBoletaOrdinaria: Boolean(c.excluirBoletaOrdinaria || c.excluir_boleta_ordinaria),
    excluir_boleta_ordinaria: Boolean(c.excluirBoletaOrdinaria || c.excluir_boleta_ordinaria),
    origenCalculo: c.origenCalculo || c.origen_calculo || c.origen || null,
    origen_calculo: c.origenCalculo || c.origen_calculo || c.origen || null,
    exportable_plame: c.exportable_plame,
    absorberEn0121: Boolean(c.absorberEn0121),
    fusionarEn0705: Boolean(c.fusionarEn0705),
    excluirRem: Boolean(c.excluirRem || c.exportable_plame === false),
  };
}

function compactarGrupoConceptos(grupo) {
  if (!grupo || typeof grupo !== 'object') return { ingresos: [], descuentos: [], aportes: [] };
  const mapArr = (arr) => (Array.isArray(arr) ? arr.map(compactarConcepto).filter(Boolean) : []);
  return {
    ingresos: mapArr(grupo.ingresos || grupo.dataIngresos),
    descuentos: mapArr(grupo.descuentos || grupo.dataDescuentos),
    aportes: mapArr(grupo.aportes || grupo.dataAportes),
  };
}

function compactarConfigTareo(cfg) {
  if (!cfg || typeof cfg !== 'object') return null;
  return {
    tipo_tareo: cfg.tipo_tareo ?? null,
    modo_horas_simple: cfg.modo_horas_simple || 'reales',
    es_simple: Boolean(cfg.es_simple || Number(cfg.tipo_tareo) === 1),
    es_detallado: Boolean(cfg.es_detallado),
    descontar_descansos: Boolean(cfg.descontar_descansos),
  };
}

function esConceptoViaticoSnap(c) {
  const codigo = String(c?.codigo || c?.codigo_plame || c?.codigoPlame || '').trim();
  const desc = String(c?.descripcion || c?.nombre || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (desc.includes('viatico') && desc.includes('pagado')) return false;
  if (codigo === '0917') return true;
  return desc.includes('condiciones de trabajo');
}

function viaticoInternoDesdeTareos(tareos) {
  const porFecha = new Map();
  let descripcion = 'CONDICIONES DE TRABAJO';
  (tareos || []).forEach((t) => {
    const fechaKey = String(t?.fecha || '').split('T')[0];
    (t.conceptos || []).forEach((c) => {
      if (!esConceptoViaticoSnap(c)) return;
      if (!conceptoTareoAplicaACanal(c, 'interna', t.conceptos || [])) return;
      const v = Number(c.monto) || 0;
      if (v <= 0) return;
      const prev = porFecha.get(fechaKey) || 0;
      if (v > prev) porFecha.set(fechaKey, v);
      if (c.descripcion) descripcion = c.descripcion;
    });
  });
  const monto = [...porFecha.values()].reduce((s, m) => s + m, 0);
  if (monto <= 0) return null;
  return {
    id: 'snap_0917',
    codigo: '0917',
    codigo_plame: '0917',
    codigoPlame: '0917',
    descripcion,
    monto,
    valor: monto,
    tipo_concepto: 'ingreso',
    origenCalculo: 'viatico',
    origen_calculo: 'viatico',
    excluirBoletaOrdinaria: true,
    excluir_boleta_ordinaria: true,
  };
}

function asegurarViaticoInternoEnGrupo(grupo, tareos) {
  const base = grupo && typeof grupo === 'object'
    ? grupo
    : { ingresos: [], descuentos: [], aportes: [] };
  const ingresos = [...(base.ingresos || [])];
  const viaTareo = viaticoInternoDesdeTareos(tareos);
  const idx = ingresos.findIndex(esConceptoViaticoSnap);
  if (idx >= 0) {
    const montoBoleta = Number(ingresos[idx].monto) || 0;
    ingresos[idx] = {
      ...ingresos[idx],
      origenCalculo: ingresos[idx].origenCalculo || ingresos[idx].origen_calculo || 'viatico',
      origen_calculo: ingresos[idx].origen_calculo || ingresos[idx].origenCalculo || 'viatico',
      excluirBoletaOrdinaria: true,
      excluir_boleta_ordinaria: true,
      // Si la boleta ya trae 0917, no pisar con la suma de tareos (evita x2 al cerrar dual).
      monto: montoBoleta > 0 ? montoBoleta : (viaTareo?.monto || 0),
    };
    ingresos[idx].valor = ingresos[idx].monto;
  } else if (viaTareo) {
    ingresos.push(viaTareo);
  }
  return { ...base, ingresos };
}

function compactarTareos(tareos, configuracionTareo = null) {
  if (!Array.isArray(tareos)) return [];
  return tareos.map((t) => {
    if (!t || typeof t !== 'object') return null;
    const conceptos = Array.isArray(t.conceptos)
      ? t.conceptos.map((c) => {
          const observaciones = String(c.observaciones || c.description || '').trim();
          const fila = {
            tipo_boleta: c.tipo_boleta || 'plame',
            tipo_concepto: c.tipo_concepto || null,
            codigo: c.plame_configuracion?.codigo_plame || c.codigo_plame || c.codigo || null,
            descripcion: c.plame_configuracion?.descripcion || c.descripcion || null,
            monto: Number(c.monto) || 0,
            plame_configuracion_id: c.plame_configuracion_id || c.plame_configuracion?.id || null,
          };
          if (observaciones) fila.observaciones = observaciones;
          return fila;
        })
      : [];
    const horasReales = Number(t.horas_trabajadas || t.horasTrabajadas || 0);
    const observacionesDia = String(t.observaciones || t.observacion || '').trim();
    return {
      fecha: ymd(t.fecha),
      tipo_evento: t.tipo_evento || t.tipoEvento || null,
      ...(observacionesDia ? { observaciones: observacionesDia } : {}),
      horas_trabajadas: horasReales,
      horas_planilla: Number(
        t.horas_planilla != null
          ? t.horas_planilla
          : horasTrabajadasEfectivas(t, configuracionTareo)
      ),
      hora_entrada: t.hora_entrada || null,
      hora_salida: t.hora_salida || null,
      conceptos,
    };
  }).filter((t) => t && t.fecha);
}

function compactarBoleta(boleta) {
  const data = boleta?.data || boleta;
  if (!data || typeof data !== 'object') return null;
  const grupo = compactarGrupoConceptos({
    ingresos: data.dataIngresos,
    descuentos: data.dataDescuentos,
    aportes: data.dataAportes,
  });
  return {
    success: true,
    data: {
      dataIngresos: grupo.ingresos,
      dataDescuentos: grupo.descuentos,
      dataAportes: grupo.aportes,
      totales: data.totales || null,
      dias_tramo: data.dias_tramo ?? boleta?.dias_tramo ?? null,
    },
    dias_tramo: boleta?.dias_tramo ?? data.dias_tramo ?? null,
    sueldo_partido: boleta?.sueldo_partido || null,
  };
}

function extraerDescuentosVista(emp) {
  const deBoleta = emp?.boleta_calculada?.data?.dataDescuentos || [];
  const deSep = emp?.conceptos_separados?.descuentos
    || emp?.conceptos?.descuentos
    || [];
  const fuente = deBoleta.length ? deBoleta : deSep;
  const vistos = new Set();
  const out = [];
  fuente.forEach((c, index) => {
    const row = compactarConcepto(c, index);
    if (!row) return;
    const codigo = String(row.codigo || '').toUpperCase();
    if (codigo !== 'DOMJ' && codigo !== 'TARJ') return;
    if ((Number(row.monto) || 0) <= 0 || vistos.has(codigo)) return;
    vistos.add(codigo);
    out.push({
      ...row,
      codigo,
      codigo_plame: codigo,
      codigoPlame: codigo,
      descripcion: row.descripcion || (codigo === 'DOMJ' ? 'DESCUENTO DOMINICAL' : 'TARDANZAS'),
      valor: Number(row.monto) || 0,
      exportable_plame: false,
      excluirRem: true,
    });
  });
  return out;
}

function numOrNull(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Foto compacta del cálculo CONSU para que el cierre no vuelva a dividir entre el tareo completo. */
export function extraerCalculoConsuCierre(emp, snap = {}) {
  const prev = snap?.calculo_construccion && typeof snap.calculo_construccion === 'object'
    ? snap.calculo_construccion
    : {};
  const calc = (emp?.calculoConstruccionCivil && typeof emp.calculoConstruccionCivil === 'object')
    ? emp.calculoConstruccionCivil
    : ((emp?.calculo_construccion && typeof emp.calculo_construccion === 'object')
      ? emp.calculo_construccion
      : {});
  const resumenSrc = (calc.resumen && typeof calc.resumen === 'object') ? calc.resumen : (prev.resumen || {});
  const periodoSrc = (calc.periodoCalculo && typeof calc.periodoCalculo === 'object')
    ? calc.periodoCalculo
    : (prev.periodoCalculo || {});
  const totSrc = (emp?.totales && typeof emp.totales === 'object')
    ? emp.totales
    : (prev.totales || {});

  const diasAsistencia = numOrNull(resumenSrc.diasAsistencia);
  const diasComputables = numOrNull(resumenSrc.diasComputables);
  const diasNeto = numOrNull(totSrc.diasNeto) ?? diasAsistencia ?? diasComputables;
  const netoAPagar = numOrNull(totSrc.netoAPagar);
  const netoPorDia = numOrNull(totSrc.netoPorDia);
  const fechaInicio = ymd(resumenSrc.fechaInicio || periodoSrc.fechaInicio);
  const fechaFin = ymd(resumenSrc.fechaFin || periodoSrc.fechaFin);
  const categoria = calc.parametrosUsados?.categoria
    || calc.ficha?.categoria
    || prev.categoria
    || null;
  const overridePeriodo = Boolean(
    resumenSrc.overridePeriodo || periodoSrc.override || prev.resumen?.overridePeriodo
  );

  const tiene = Boolean(
    diasAsistencia
    || diasComputables
    || diasNeto
    || netoAPagar != null
    || fechaInicio
    || fechaFin
    || categoria
    || (Array.isArray(calc.detalleDias) && calc.detalleDias.length)
    || emp?.fecha_calculo_desde
    || emp?.fecha_calculo_hasta
    || prev.totales
    || prev.resumen
  );
  if (!tiene) return null;

  return {
    categoria,
    resumen: {
      diasAsistencia,
      diasComputables,
      diasCalendarioVinculo: numOrNull(resumenSrc.diasCalendarioVinculo) ?? numOrNull(prev.resumen?.diasCalendarioVinculo),
      diasDescansoRemunerado: numOrNull(resumenSrc.diasDescansoRemunerado) ?? numOrNull(prev.resumen?.diasDescansoRemunerado),
      diasComputablesGratificacion: numOrNull(resumenSrc.diasComputablesGratificacion)
        ?? numOrNull(prev.resumen?.diasComputablesGratificacion),
      fechaInicio,
      fechaFin,
      overridePeriodo,
      tipoGratificacion: resumenSrc.tipoGratificacion || prev.resumen?.tipoGratificacion || null,
    },
    totales: {
      netoAPagar,
      netoPorDia,
      diasNeto,
      totalIngresos: numOrNull(totSrc.totalIngresos),
      totalDescuentos: numOrNull(totSrc.totalDescuentos),
      totalAportes: numOrNull(totSrc.totalAportes),
    },
    periodoCalculo: (fechaInicio || fechaFin)
      ? { fechaInicio, fechaFin, override: overridePeriodo }
      : (prev.periodoCalculo || null),
  };
}

/** Conserva días y neto/día CONSU al armar los totales de una planilla cerrada. */
export function fusionarTotalesVistaCerrada(totalesVista = {}, emp = {}) {
  const consuTot = (emp?.totales && typeof emp.totales === 'object') ? emp.totales : {};
  const resumen = emp?.calculoConstruccionCivil?.resumen || {};
  const snapTot = emp?.snapshot_interna?.calculo_construccion?.totales || {};
  const dias = Number(
    consuTot.diasNeto ?? snapTot.diasNeto ?? resumen.diasAsistencia ?? resumen.diasComputables
  ) || 0;
  const netoPorDia = consuTot.netoPorDia ?? snapTot.netoPorDia;
  return {
    ...totalesVista,
    ...(dias > 0 ? { diasNeto: dias } : {}),
    ...(netoPorDia != null && netoPorDia !== '' ? { netoPorDia: Number(netoPorDia) } : {}),
  };
}

function anexarVistaAlSnapshot(snap, emp) {
  const base = snap && typeof snap === 'object' ? { ...snap } : {};
  const vista = extraerDescuentosVista(emp);
  if (vista.length) base.descuentos_vista = vista;
  const pago = emp?.pagoJornal || emp?.pago_jornal || base.pago_jornal || null;
  if (pago && typeof pago === 'object') base.pago_jornal = pago;
  const aplicar = emp?.aplicar_descuento_dominical
    ?? emp?.datosSalariales?.aplicarDescuentoDominical
    ?? base.aplicar_descuento_dominical
    ?? null;
  if (aplicar !== undefined) base.aplicar_descuento_dominical = aplicar;
  const desde = ymd(emp?.fecha_calculo_desde || base.fecha_calculo_desde);
  const hasta = ymd(emp?.fecha_calculo_hasta || base.fecha_calculo_hasta);
  if (desde) base.fecha_calculo_desde = desde;
  if (hasta) base.fecha_calculo_hasta = hasta;
  const consu = extraerCalculoConsuCierre(emp, base);
  if (consu) base.calculo_construccion = consu;
  return base;
}

export function armarSnapshotInternaCierre(emp) {
  if (!emp || typeof emp !== 'object') return null;
  const tipo = String(emp.tipo_planilla || emp.datosSalariales?.tipoPlanilla || '').toLowerCase();
  const sep = emp.conceptos_separados_internos || emp.conceptosInternos || null;
  const tieneInterna = tipo === 'dual' || tipo === 'solo_interna'
    || Number(emp.sueldo_interno || emp.datosSalariales?.sueldoInterno || 0) > 0
    || Boolean(emp.boleta_interna_calculada)
    || Boolean(sep && ((sep.ingresos || []).length + (sep.descuentos || []).length + (sep.aportes || []).length));

  if (!tieneInterna && !(emp.fecha_ingreso_interna || emp.historial_laboral_interno?.length)) {
    const soloVista = anexarVistaAlSnapshot({}, emp);
    if (
      soloVista.descuentos_vista?.length
      || soloVista.pago_jornal
      || soloVista.fecha_calculo_desde
      || soloVista.fecha_calculo_hasta
      || soloVista.calculo_construccion
    ) {
      return soloVista;
    }
    return null;
  }

  const grupoBase = compactarGrupoConceptos(sep || {});
  const cfgTareo = compactarConfigTareo(
    emp.configuracion_tareo || emp.configuracionTareo || null
  );
  const tareosPlame = compactarTareos(emp.tareosEmpleado, cfgTareo);
  const tareosInterna = compactarTareos(
    emp.tareosEmpleadoInterna?.length ? emp.tareosEmpleadoInterna : emp.tareosEmpleado,
    cfgTareo
  );
  const tareosParaViatico = tareosInterna.length ? tareosInterna : tareosPlame;
  const grupo = asegurarViaticoInternoEnGrupo(grupoBase, tareosParaViatico);
  const boletaInterna = compactarBoleta(emp.boleta_interna_calculada);
  const grupoDesdeBoleta = boletaInterna?.data
    ? compactarGrupoConceptos({
        ingresos: boletaInterna.data.dataIngresos,
        descuentos: boletaInterna.data.dataDescuentos,
        aportes: boletaInterna.data.dataAportes,
      })
    : null;
  const grupoFinal = grupo.ingresos.length
    ? grupo
    : asegurarViaticoInternoEnGrupo(grupoDesdeBoleta || grupo, tareosInterna);
  const totalesDesdeGrupo = (g) => {
    const ti = (g.ingresos || []).reduce((s, c) => (
      c.excluirBoletaOrdinaria || c.excluir_boleta_ordinaria ? s : s + Number(c.monto || 0)
    ), 0);
    const td = (g.descuentos || []).reduce((s, c) => s + Number(c.monto || 0), 0);
    const ta = (g.aportes || []).reduce((s, c) => s + Number(c.monto || 0), 0);
    return { totalIngresos: ti, totalDescuentos: td, totalAportes: ta, netoAPagar: ti - td };
  };
  return anexarVistaAlSnapshot({
    tipo_planilla: emp.tipo_planilla || emp.datosSalariales?.tipoPlanilla || null,
    sueldo_interno: emp.sueldo_interno ?? emp.datosSalariales?.sueldoInterno ?? null,
    fecha_ingreso_interna: ymd(emp.fecha_ingreso_interna || emp.fechaIngresoInterna),
    historial_laboral: compactarHistorial(emp.historial_laboral || emp.historialLaboral),
    historial_laboral_interno: compactarHistorial(
      emp.historial_laboral_interno || emp.historialLaboralInterno
    ),
    omitir_descuentos_plame_interna: Boolean(
      emp.omitir_descuentos_plame_interna
      || emp.datosSalariales?.omitirDescuentosPlameInterna
    ),
    boleta_interna_partida: Boolean(emp.boleta_interna_partida),
    sueldo_partido: emp.sueldo_partido || null,
    dias_interna_1: emp.dias_interna_1 ?? null,
    dias_interna_2: emp.dias_interna_2 ?? null,
    sueldo_interno_1: emp.sueldo_interno_1 ?? emp.sueldoInterno1 ?? null,
    sueldo_interno_2: emp.sueldo_interno_2 ?? emp.sueldoInterno2 ?? null,
    conceptos_separados_internos: grupoFinal,
    conceptos_separados_internos_1: emp.conceptos_separados_internos_1
      ? compactarGrupoConceptos(emp.conceptos_separados_internos_1)
      : null,
    conceptos_separados_internos_2: emp.conceptos_separados_internos_2
      ? compactarGrupoConceptos(emp.conceptos_separados_internos_2)
      : null,
    boleta_interna_calculada: boletaInterna,
    tareosEmpleado: tareosPlame,
    tareosEmpleadoInterna: tareosInterna,
    configuracion_tareo: cfgTareo,
    monto_viatico_standard: emp.monto_viatico_standard
      ?? emp.montoViaticoStandard
      ?? emp.datosSalariales?.montoViaticoStandard
      ?? null,
    totales_internos: emp.totalesInternos || emp.totales_internos || totalesDesdeGrupo(grupoFinal),
  }, emp);
}

export function aplicarSnapshotInternaCierre(emp, snap) {
  const fuente = snap && typeof snap === 'object' ? snap : (emp?.snapshot_interna || null);
  if (!fuente || typeof fuente !== 'object') return emp || {};

  const tareosVia = (fuente.tareosEmpleadoInterna?.length
    ? fuente.tareosEmpleadoInterna
    : fuente.tareosEmpleado) || [];
  const sep = asegurarViaticoInternoEnGrupo(
    fuente.conceptos_separados_internos || compactarGrupoConceptos({}),
    tareosVia
  );
  const internList = [
    ...(sep.ingresos || []),
    ...(sep.descuentos || []),
    ...(sep.aportes || []),
  ];

  const descuentosVista = Array.isArray(fuente.descuentos_vista) ? fuente.descuentos_vista : [];
  const boletaBase = emp?.boleta_calculada?.data || {};
  const descuentosBoleta = Array.isArray(boletaBase.dataDescuentos) ? [...boletaBase.dataDescuentos] : [];
  descuentosVista.forEach((vista) => {
    const codigo = String(vista?.codigo || vista?.codigo_plame || '').toUpperCase();
    if (!codigo) return;
    const ya = descuentosBoleta.some(
      (d) => String(d?.codigo || d?.codigo_plame || '').toUpperCase() === codigo
    );
    if (ya) return;
    descuentosBoleta.push({
      codigo,
      codigo_plame: codigo,
      descripcion: vista.descripcion || (codigo === 'DOMJ' ? 'DESCUENTO DOMINICAL' : 'TARDANZAS'),
      valor: Number(vista.valor ?? vista.monto) || 0,
      base_imponible: Number(vista.base_imponible) || 0,
      origen_calculo: vista.origen_calculo || vista.origenCalculo || null,
      exportable_plame: false,
      absorberEn0121: Boolean(vista.absorberEn0121),
      fusionarEn0705: Boolean(vista.fusionarEn0705),
      excluirRem: true,
    });
  });

  const consu = fuente.calculo_construccion && typeof fuente.calculo_construccion === 'object'
    ? fuente.calculo_construccion
    : null;
  const totConsu = (consu?.totales && typeof consu.totales === 'object') ? consu.totales : {};
  const totEmp = (emp?.totales && typeof emp.totales === 'object') ? emp.totales : {};
  const totalesRestaurados = { ...totEmp };
  Object.entries(totConsu).forEach(([k, v]) => {
    if (v != null && v !== '') totalesRestaurados[k] = v;
  });
  const calcRestaurado = consu
    ? {
      ...(emp?.calculoConstruccionCivil || {}),
      resumen: { ...(emp?.calculoConstruccionCivil?.resumen || {}), ...(consu.resumen || {}) },
      periodoCalculo: consu.periodoCalculo
        || emp?.calculoConstruccionCivil?.periodoCalculo
        || null,
      parametrosUsados: {
        ...(emp?.calculoConstruccionCivil?.parametrosUsados || {}),
        ...(consu.categoria ? { categoria: consu.categoria } : {}),
      },
    }
    : emp?.calculoConstruccionCivil;

  return {
    ...emp,
    tipo_planilla: fuente.tipo_planilla ?? emp.tipo_planilla,
    sueldo_interno: fuente.sueldo_interno ?? emp.sueldo_interno,
    fecha_ingreso_interna: fuente.fecha_ingreso_interna ?? emp.fecha_ingreso_interna,
    historial_laboral: (fuente.historial_laboral?.length
      ? fuente.historial_laboral
      : emp.historial_laboral) || [],
    historial_laboral_interno: (fuente.historial_laboral_interno?.length
      ? fuente.historial_laboral_interno
      : emp.historial_laboral_interno) || [],
    omitir_descuentos_plame_interna: Boolean(fuente.omitir_descuentos_plame_interna),
    boleta_interna_partida: Boolean(fuente.boleta_interna_partida),
    sueldo_partido: fuente.sueldo_partido ?? emp.sueldo_partido ?? null,
    dias_interna_1: fuente.dias_interna_1 ?? emp.dias_interna_1 ?? null,
    dias_interna_2: fuente.dias_interna_2 ?? emp.dias_interna_2 ?? null,
    sueldo_interno_1: fuente.sueldo_interno_1 ?? emp.sueldo_interno_1 ?? null,
    sueldo_interno_2: fuente.sueldo_interno_2 ?? emp.sueldo_interno_2 ?? null,
    sueldoInterno1: fuente.sueldo_interno_1 ?? emp.sueldoInterno1 ?? null,
    sueldoInterno2: fuente.sueldo_interno_2 ?? emp.sueldoInterno2 ?? null,
    conceptos_separados_internos: sep,
    conceptos_internos: internList,
    conceptosInternos: sep,
    conceptos_separados_internos_1: fuente.conceptos_separados_internos_1 || null,
    conceptos_separados_internos_2: fuente.conceptos_separados_internos_2 || null,
    conceptosInternos1: fuente.conceptos_separados_internos_1 || emp.conceptosInternos1 || null,
    conceptosInternos2: fuente.conceptos_separados_internos_2 || emp.conceptosInternos2 || null,
    boleta_interna_calculada: fuente.boleta_interna_calculada || emp.boleta_interna_calculada || null,
    tareosEmpleado: (fuente.tareosEmpleado?.length ? fuente.tareosEmpleado : emp.tareosEmpleado) || [],
    tareosEmpleadoInterna: (fuente.tareosEmpleadoInterna?.length
      ? fuente.tareosEmpleadoInterna
      : emp.tareosEmpleadoInterna) || [],
    configuracion_tareo: fuente.configuracion_tareo || emp.configuracion_tareo || null,
    monto_viatico_standard: fuente.monto_viatico_standard
      ?? emp.monto_viatico_standard
      ?? emp.montoViaticoStandard
      ?? null,
    montoViaticoStandard: fuente.monto_viatico_standard
      ?? emp.montoViaticoStandard
      ?? emp.monto_viatico_standard
      ?? null,
    totalesInternos: fuente.totales_internos || emp.totalesInternos || null,
    pagoJornal: fuente.pago_jornal || emp.pagoJornal || emp.pago_jornal || null,
    pago_jornal: fuente.pago_jornal || emp.pago_jornal || emp.pagoJornal || null,
    aplicar_descuento_dominical: fuente.aplicar_descuento_dominical
      ?? emp.aplicar_descuento_dominical
      ?? null,
    fecha_calculo_desde: fuente.fecha_calculo_desde ?? emp.fecha_calculo_desde ?? null,
    fecha_calculo_hasta: fuente.fecha_calculo_hasta ?? emp.fecha_calculo_hasta ?? null,
    calculoConstruccionCivil: calcRestaurado || emp?.calculoConstruccionCivil || null,
    totales: Object.keys(totalesRestaurados).length ? totalesRestaurados : emp?.totales,
    descuentos_vista: descuentosVista,
    boleta_calculada: {
      ...(emp.boleta_calculada || {}),
      data: {
        ...boletaBase,
        dataDescuentos: descuentosBoleta,
      },
    },
  };
}
